import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatNumber } from "../utils/format";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";

export default function StockCountForm({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [stockCount, setStockCount] = useState(null);
  const [isGrouped, setIsGrouped] = useState(false);
  const [sourceCounts, setSourceCounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStockCount, setLoadingStockCount] = useState(false);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState("");
  const [newlyAddedIndex, setNewlyAddedIndex] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateProduct, setDuplicateProduct] = useState(null);
  const [pendingItem, setPendingItem] = useState(null);
  const [allStockCounts, setAllStockCounts] = useState([]);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [selectedStockCountToAdd, setSelectedStockCountToAdd] = useState("");
  const [addingToGroup, setAddingToGroup] = useState(false);
  const [removingFromGroup, setRemovingFromGroup] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [showItemValidation, setShowItemValidation] = useState(false);
  const { errorMessage, showError, clearError } = useErrorModal();

  const [expandedProducts, setExpandedProducts] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [stockCountItems, setStockCountItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    product_id: "",
    quantity: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    items: false,
    itemQuantity: false,
  });

  useEffect(() => {
    if (!user) return;
    fetchProducts();
    fetchBrands();

    if (isEdit) {
      fetchStockCount();
    }
  }, [user, id]);

  // Fetch all stock counts when viewing a grouped count (for adding new sources)
  useEffect(() => {
    if (isGrouped && user) {
      apiFetch("/stock-counts")
        .then(setAllStockCounts)
        .catch((err) => console.error("Fout bij ophalen alle tellingen:", err));
    }
  }, [isGrouped, user]);

  const fetchStockCount = () => {
    setLoadingStockCount(true);
    apiFetch(`/stock-counts/${id}`)
      .then((data) => {
        setStockCount(data);
        setIsGrouped(data.is_grouped || false);
        setSourceCounts(data.source_counts || []);
        let dateValue = data.date;
        if (dateValue && dateValue.includes('T')) {
          dateValue = dateValue.split('T')[0];
        }
        setFormData({
          name: data.name,
          date: dateValue,
          notes: data.notes || "",
        });

        // Fetch items separately
        apiFetch(`/stock-counts/${id}/items`)
          .then((items) => {
            setStockCountItems(
              items.map((item) => ({
                product_id: item.product_id,
                product_name: item.product_name,
                quantity: item.quantity,
                location_id: item.location_id,
                location_name: item.location_name,
                location_code: item.location_code,
              }))
            );
          })
          .catch((err) => {
            console.error("Fout bij ophalen voorraadtelling items:", err);
          });
      })
      .catch((err) => {
        console.error("Fout bij ophalen voorraadtelling:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingStockCount(false));
  };

  const fetchProducts = () => {
    apiFetch("/products?sort=product_name&order=asc&limit=1000")
      .then((data) => {
        // Handle new paginated response format
        if (data.items) {
          setProducts(data.items);
        } else {
          setProducts(data);
        }
      })
      .catch((err) => {
        console.error("Fout bij ophalen producten:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const fetchBrands = () => {
    apiFetch("/brands?sort=name&order=asc")
      .then(setBrands)
      .catch((err) => {
        console.error("Fout bij ophalen merken:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      });
  };

  const handleAddItem = () => {
    if (!currentItem.product_id || !currentItem.quantity) {
      setShowItemValidation(true);
      setValidationErrors(prev => ({ ...prev, itemQuantity: !currentItem.quantity }));
      return;
    }
    setShowItemValidation(false);
    setValidationErrors(prev => ({ ...prev, itemQuantity: false }));

    const product = products.find((p) => p.id === parseInt(currentItem.product_id));
    const productId = parseInt(currentItem.product_id);
    const newQuantity = parseFloat(currentItem.quantity);

    // Check if product already exists in stock count items
    const existingItemIndex = stockCountItems.findIndex(item => item.product_id === productId);

    if (existingItemIndex !== -1) {
      // Product exists - show confirmation modal
      setDuplicateProduct({
        ...stockCountItems[existingItemIndex],
        index: existingItemIndex
      });
      setPendingItem({
        product_id: productId,
        product_name: product?.product_name || "",
        quantity: newQuantity,
      });
      setShowDuplicateModal(true);
    } else {
      // New product - add to list
      const newItem = {
        product_id: productId,
        product_name: product?.product_name || "",
        quantity: newQuantity,
      };
      setStockCountItems([...stockCountItems, newItem]);

      // Highlight the newly added item
      setNewlyAddedIndex(stockCountItems.length);
      setTimeout(() => setNewlyAddedIndex(null), 1000);

      setCurrentItem({
        product_id: "",
        quantity: "",
      });
    }
  };

  const handleDuplicateReplace = () => {
    // Replace with new quantity
    const updatedItems = [...stockCountItems];
    updatedItems[duplicateProduct.index] = {
      ...updatedItems[duplicateProduct.index],
      quantity: pendingItem.quantity,
    };
    setStockCountItems(updatedItems);

    // Highlight the updated item
    setNewlyAddedIndex(duplicateProduct.index);
    setTimeout(() => setNewlyAddedIndex(null), 1000);

    // Close modal and reset
    setShowDuplicateModal(false);
    setDuplicateProduct(null);
    setPendingItem(null);
    setCurrentItem({
      product_id: "",
      quantity: "",
    });
  };

  const handleDuplicateAdd = () => {
    // Add quantities together
    const updatedItems = [...stockCountItems];
    updatedItems[duplicateProduct.index] = {
      ...updatedItems[duplicateProduct.index],
      quantity: duplicateProduct.quantity + pendingItem.quantity,
    };
    setStockCountItems(updatedItems);

    // Highlight the updated item
    setNewlyAddedIndex(duplicateProduct.index);
    setTimeout(() => setNewlyAddedIndex(null), 1000);

    // Close modal and reset
    setShowDuplicateModal(false);
    setDuplicateProduct(null);
    setPendingItem(null);
    setCurrentItem({
      product_id: "",
      quantity: "",
    });
  };

  const handleDuplicateCancel = () => {
    // Just close modal without making changes
    setShowDuplicateModal(false);
    setDuplicateProduct(null);
    setPendingItem(null);
  };

  const handleRemoveItem = (index) => {
    setStockCountItems(stockCountItems.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowValidation(true);

    const errors = {
      name: !formData.name,
      items: stockCountItems.length === 0,
      itemQuantity: false,
    };

    setValidationErrors(errors);

    if (errors.name || errors.items) {
      return;
    }

    setLoading(true);

    const payload = {
      name: formData.name,
      date: formData.date,
      notes: formData.notes || null,
      items: stockCountItems.map((item) => {
        const product = products.find(p => p.id === item.product_id);
        return {
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: product?.purchase_price_incl_discount || null,
          location_id: null,
          notes: null
        };
      }),
    };

    const method = isEdit ? "PATCH" : "POST";
    const url = isEdit ? `/stock-counts/${id}` : "/stock-counts";

    // For edit mode, we need to handle both stock count update and items replacement
    if (isEdit) {
      // First update the stock count metadata
      const metadataPayload = {
        name: formData.name,
        date: formData.date,
        notes: formData.notes || null,
      };

      apiFetch(`/stock-counts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(metadataPayload),
      })
        .then(() => {
          // Then replace all items
          return apiFetch(`/stock-counts/${id}/items`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload.items)
          });
        })
        .then(() => {
          navigate("/stock-counts");
        })
        .catch((err) => {
          console.error("Fout bij opslaan voorraadtelling:", err);
          showError("Er is een fout opgetreden bij het opslaan van de voorraadtelling.");
          if (err.message.includes("403")) setUnauthorized(true);
        })
        .finally(() => setLoading(false));
    } else {
      // For new stock counts, create with items
      apiFetch(url, {
        method,
        body: JSON.stringify(payload),
      })
        .then(() => {
          navigate("/stock-counts");
        })
        .catch((err) => {
          console.error("Fout bij opslaan voorraadtelling:", err);
          showError("Er is een fout opgetreden bij het opslaan van de voorraadtelling.");
          if (err.message.includes("403")) setUnauthorized(true);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleBrandFilterChange = (brandId) => {
    setSelectedBrandFilter(brandId);
  };

  // Get available stock counts that can be added to this group
  const availableForGroup = allStockCounts.filter(
    (sc) =>
      !sc.is_grouped && // Can't add grouped counts
      !sourceCounts.some((source) => source.id === sc.id) // Not already in group
  );

  const handleAddToGroup = () => {
    if (!selectedStockCountToAdd) return;

    setAddingToGroup(true);
    apiFetch(`/stock-counts/group/${id}/add/${selectedStockCountToAdd}`, {
      method: "POST",
    })
      .then(() => {
        setShowAddToGroupModal(false);
        setSelectedStockCountToAdd("");
        // Refresh the stock count data
        fetchStockCount();
      })
      .catch((err) => {
        console.error("Fout bij toevoegen aan groep:", err);
        showError(err.message || "Er is een fout opgetreden bij het toevoegen aan de groep.");
      })
      .finally(() => setAddingToGroup(false));
  };

  const handleRemoveFromGroup = (sourceId) => {
    if (!window.confirm("Weet je zeker dat je deze telling uit de groep wilt verwijderen?")) {
      return;
    }

    setRemovingFromGroup(sourceId);
    apiFetch(`/stock-counts/group/${id}/remove/${sourceId}`, {
      method: "DELETE",
    })
      .then(() => {
        // Refresh the stock count data
        fetchStockCount();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen uit groep:", err);
        showError(err.message || "Er is een fout opgetreden bij het verwijderen uit de groep.");
      })
      .finally(() => setRemovingFromGroup(null));
  };

  const filteredProducts = selectedBrandFilter
    ? products.filter(product => product.brand_id === parseInt(selectedBrandFilter))
    : products;

  if (unauthorized) return <Navigate to="/unauthorized" replace />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{isEdit ? "Voorraadtelling Bewerken" : "Nieuwe Voorraadtelling"}</h1>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Stock Count Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Tellinggegevens</h2>
            {isGrouped && (
              <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 rounded">
                Gegroepeerde telling
              </span>
            )}
          </div>

          {/* Show source counts for grouped counts */}
          {isGrouped && sourceCounts.length > 0 && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-800">
                  Deze telling is een groepering van de volgende tellingen:
                </span>
                <button
                  type="button"
                  onClick={() => setShowAddToGroupModal(true)}
                  className="px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  + Telling toevoegen
                </button>
              </div>
              <ul className="text-sm space-y-1">
                {sourceCounts.map((sc) => (
                  <li key={sc.id} className="flex items-center justify-between py-1 px-2 bg-white rounded border border-purple-100">
                    <button
                      type="button"
                      onClick={() => navigate(`/stock-counts/${sc.id}`)}
                      className="text-sm text-purple-600 hover:text-purple-800 hover:underline font-normal"
                    >
                      {sc.name} ({new Date(sc.date).toLocaleDateString("nl-BE")})
                    </button>
                    {sourceCounts.length > 2 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveFromGroup(sc.id)}
                        disabled={removingFromGroup === sc.id}
                        className="ml-2 px-2 py-0.5 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                      >
                        {removingFromGroup === sc.id ? "..." : "Verwijderen"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {sourceCounts.length <= 2 && (
                <span className="text-xs text-purple-500 mt-2 block italic">
                  Een groep moet minimaal 2 tellingen bevatten.
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naam *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (e.target.value) setValidationErrors(prev => ({ ...prev, name: false }));
                }}
                className={`w-full px-3 py-2 border rounded transition-colors ${
                  validationErrors.name || (showValidation && !formData.name)
                    ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                }`}
                placeholder="Bijv. Voorraadtelling januari 2025"
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600">Vul een naam in voor de voorraadtelling</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Datum *
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full max-w-xs px-3 py-2 border rounded"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opmerkingen
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded"
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Stock Count Items */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Productlijnen</h2>

          {/* Add Item Form - Hidden for grouped counts */}
          {!isGrouped && (
            <div className="mb-4 p-4 bg-gray-50 rounded border">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Nieuw product toevoegen</h3>
              <div className="flex gap-2">
                <div className="w-48">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Merk</label>
                  <select
                    value={selectedBrandFilter}
                    onChange={(e) => handleBrandFilterChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  >
                    <option value="">Alle merken</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Product</label>
                  <select
                    value={currentItem.product_id}
                    onChange={(e) => setCurrentItem({ ...currentItem, product_id: e.target.value })}
                    className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                      showItemValidation && !currentItem.product_id
                        ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }`}
                  >
                    <option value="">-- Selecteer product --</option>
                    {filteredProducts.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.product_name} {product.sku ? `(${product.sku})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-32">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Aantal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={currentItem.quantity}
                    onChange={(e) => {
                      setCurrentItem({ ...currentItem, quantity: e.target.value });
                      if (e.target.value) setValidationErrors(prev => ({ ...prev, itemQuantity: false }));
                    }}
                    className={`w-full px-3 py-2 border rounded text-sm transition-colors ${
                      validationErrors.itemQuantity || (showItemValidation && !currentItem.quantity)
                        ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }`}
                    placeholder="0"
                  />
                </div>
                <div className="w-32 flex items-end">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm whitespace-nowrap"
                  >
                    Toevoegen
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info message for grouped counts */}
          {isGrouped && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800">
                Dit is een gegroepeerde telling. Producten toevoegen of verwijderen is niet mogelijk.
                De onderstaande lijst toont de som van alle gegroepeerde tellingen per product.
              </p>
            </div>
          )}

          {/* Validation error for empty items */}
          {validationErrors.items && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-600">Voeg minstens één product toe aan de voorraadtelling.</p>
            </div>
          )}

          {/* Loading Indicator */}
          {loadingStockCount && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Tellinggegevens laden...</span>
            </div>
          )}

          {/* Items Table */}
          {!loadingStockCount && stockCountItems.length > 0 && (() => {
            // Group items by product_id
            const hasLocationData = stockCountItems.some(item => item.location_id);
            const grouped = {};
            stockCountItems.forEach((item, index) => {
              if (!grouped[item.product_id]) {
                grouped[item.product_id] = {
                  product_id: item.product_id,
                  product_name: item.product_name,
                  totalQuantity: 0,
                  locations: [],
                  indices: [],
                };
              }
              grouped[item.product_id].totalQuantity += item.quantity;
              grouped[item.product_id].indices.push(index);
              if (item.location_id) {
                grouped[item.product_id].locations.push({
                  location_id: item.location_id,
                  location_name: item.location_name,
                  location_code: item.location_code,
                  quantity: item.quantity,
                  index,
                });
              }
            });
            const groupedProducts = Object.values(grouped);

            return (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Product</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-700">Aantal</th>
                    {!isGrouped && (
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-700">Acties</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedProducts.map((group) => {
                    const hasMultipleLocations = group.locations.length > 1;
                    const isExpanded = expandedProducts[group.product_id];
                    const isHighlighted = group.indices.some(i => i === newlyAddedIndex);

                    return (
                      <React.Fragment key={group.product_id}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                            isHighlighted ? 'bg-green-50' : ''
                          }`}
                          onClick={() => {
                            if (hasMultipleLocations && hasLocationData) {
                              setExpandedProducts(prev => ({
                                ...prev,
                                [group.product_id]: !prev[group.product_id]
                              }));
                            } else {
                              navigate(`/products/${group.product_id}`);
                            }
                          }}
                        >
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <span className="flex items-center gap-1">
                              {hasMultipleLocations && hasLocationData && (
                                <span className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700">
                                  {isExpanded ? (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  )}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/${group.product_id}`);
                                }}
                                className="text-left"
                              >
                                {group.product_name}
                              </button>
                              {hasMultipleLocations && hasLocationData && (
                                <span className="text-xs text-gray-400 ml-1">
                                  ({group.locations.length} locaties)
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900 font-medium">
                            {formatNumber(group.totalQuantity)}
                          </td>
                          {!isGrouped && (
                            <td className="px-4 py-2 text-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Remove all items for this product
                                  const indicesToRemove = new Set(group.indices);
                                  setStockCountItems(stockCountItems.filter((_, i) => !indicesToRemove.has(i)));
                                }}
                                className="text-red-600 hover:text-red-900 text-sm"
                              >
                                Verwijderen
                              </button>
                            </td>
                          )}
                        </tr>
                        {/* Expanded location rows */}
                        {isExpanded && group.locations.map((loc) => (
                          <tr
                            key={`${group.product_id}-${loc.location_id}`}
                            className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                            onClick={() => navigate(`/products/${group.product_id}`)}
                          >
                            <td className="px-4 py-1.5 text-xs text-gray-600 pl-12">
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {loc.location_name || loc.location_code}
                              </span>
                            </td>
                            <td className="px-4 py-1.5 text-xs text-right text-gray-600">
                              {formatNumber(loc.quantity)}
                            </td>
                            {!isGrouped && <td />}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/stock-counts")}
            className="px-6 py-2 border rounded hover:bg-gray-100"
            disabled={loading}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            disabled={loading}
          >
            {loading ? "Bezig..." : isEdit ? "Bijwerken" : "Opslaan"}
          </button>
        </div>
      </form>

      {/* Add to Group Modal */}
      {showAddToGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Telling toevoegen aan groep</h3>
            {availableForGroup.length === 0 ? (
              <p className="text-sm text-gray-600 mb-4">
                Er zijn geen tellingen beschikbaar om toe te voegen. Alle niet-gegroepeerde tellingen
                zijn al onderdeel van deze groep of zijn zelf gegroepeerde tellingen.
              </p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Selecteer een telling om toe te voegen aan deze groep:
                </p>
                <select
                  value={selectedStockCountToAdd}
                  onChange={(e) => setSelectedStockCountToAdd(e.target.value)}
                  className="w-full px-3 py-2 border rounded mb-4"
                >
                  <option value="">-- Selecteer een telling --</option>
                  {availableForGroup.map((sc) => (
                    <option key={sc.id} value={sc.id}>
                      {sc.name} ({new Date(sc.date).toLocaleDateString("nl-BE")})
                    </option>
                  ))}
                </select>
              </>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowAddToGroupModal(false);
                  setSelectedStockCountToAdd("");
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Annuleren
              </button>
              {availableForGroup.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddToGroup}
                  disabled={!selectedStockCountToAdd || addingToGroup}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {addingToGroup ? "Bezig..." : "Toevoegen"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal message={errorMessage} onClose={clearError} />

      {/* Duplicate Product Confirmation Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Product bestaat al</h3>
            <p className="text-sm text-gray-700 mb-4">
              Het product <strong>{duplicateProduct?.product_name}</strong> staat al in de lijst.
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <div className="text-sm">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Huidige hoeveelheid:</span>
                  <span className="font-medium">{formatNumber(duplicateProduct?.quantity)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Nieuwe hoeveelheid:</span>
                  <span className="font-medium">{formatNumber(pendingItem?.quantity)}</span>
                </div>
                <div className="border-t border-gray-300 mt-2 pt-2 flex justify-between">
                  <span className="text-gray-600">Totaal bij optelling:</span>
                  <span className="font-medium">{formatNumber((duplicateProduct?.quantity || 0) + (pendingItem?.quantity || 0))}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">Wat wil je doen?</p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleDuplicateReplace}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Vervangen (gebruik nieuwe waarde)
              </button>
              <button
                type="button"
                onClick={handleDuplicateAdd}
                className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Optellen (bij bestaande waarde)
              </button>
              <button
                type="button"
                onClick={handleDuplicateCancel}
                className="w-full px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
